import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Send, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { authApi } from '@/services/api/auth';

// Schema de validación
const schema = z.object({
    email: z.string().min(1, 'El email es requerido').email('Ingresa un email válido'),
});

type FormData = z.infer<typeof schema>;

const ForgotPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const [sent, setSent] = useState(false);
    const [serverMsg, setServerMsg] = useState<string | null>(null);

    const { register, handleSubmit, formState: { errors, isSubmitting }, setError } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: { email: '' },
    });

    const onSubmit = async (data: FormData) => {
        setServerMsg(null);
        try {
            // POST /auth/forgot-password → envía correo con link/token
            await (authApi as any).forgotPassword(data.email);
            setSent(true);
            setServerMsg('Te enviamos un correo con las instrucciones para restablecer tu contraseña.');
        } catch (err: any) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'No se pudo procesar la solicitud';
            if (err?.response?.data?.errors?.email?.[0]) {
                setError('email', { message: err.response.data.errors.email[0] });
            } else if (status === 404) {
                // correo no registrado (si tu backend lo expone)
                setError('email', { message: 'No encontramos una cuenta con este correo.' });
            } else if (status === 429) {
                setServerMsg('Has solicitado demasiadas veces. Inténtalo en unos minutos.');
            } else {
                setServerMsg(msg);
            }
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl shadow-xl p-8"
                >
                    {/* Header (mismo look & feel que Login) */}
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-flex items-center space-x-2 mb-6">
                            <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
                                <span className="text-white font-bold text-xl">T</span>
                            </div>
                            <span className="text-2xl font-bold text-gray-900">Eisgemi</span>
                        </Link>

                        <h2 className="text-3xl font-bold text-gray-900 mb-2">¿Olvidaste tu contraseña?</h2>
                        <p className="text-gray-600">Ingresa tu correo y te enviaremos un enlace para restablecerla.</p>
                    </div>

                    {!sent ? (
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            <Input
                                {...register('email')}
                                type="email"
                                label="Email"
                                placeholder="tu@email.com"
                                leftIcon={<Mail className="w-5 h-5" />}
                                error={errors.email?.message}
                                fullWidth
                            />

                            {serverMsg && (
                                <p className="text-sm text-red-600">{serverMsg}</p>
                            )}

                            <Button type="submit" loading={isSubmitting} fullWidth size="lg" rightIcon={<Send className="w-5 h-5" />}>
                                Enviarme instrucciones
                            </Button>

                            <div className="flex items-center justify-between text-sm">
                                <Link to="/login" className="text-primary-600 hover:text-primary-500 inline-flex items-center gap-1">
                                    <ArrowLeft className="w-4 h-4" /> Volver a iniciar sesión
                                </Link>
                                <Link to="/register" className="text-primary-600 hover:text-primary-500">Crear cuenta</Link>
                            </div>
                        </form>
                    ) : (
                        <div className="space-y-6 text-center">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-1">Revisa tu correo</h3>
                                <p className="text-gray-600">{serverMsg}</p>
                            </div>
                            <Button onClick={() => navigate('/login')} fullWidth size="lg">Volver al login</Button>
                            <div className="text-sm text-gray-500">
                                ¿No recibiste el correo? Revisa tu carpeta de spam o intenta de nuevo más tarde.
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;

/* =============================================================
  Servicios recomendados (auth.ts)
  —Opción preferida: **no** tocar authStore; agregar endpoints al servicio.



// Página de reset sugerida (crea /reset-password):
// - Lee ?token= de la URL
// - Form con email, nueva contraseña, confirmación
// - Llama authApi.resetPassword({ token, email, password, password_confirmation })
============================================================= */
